import React from 'react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';

export default function ParcoursUniversitairesPage() {
  const lmdGrades = [
    {
      letter: "L",
      title: "Licence (Bac +3)",
      color: "bg-emerald-600",
      desc: "Se prépare en 6 semestres (S1 à S6). Elle peut être Fondamentale (théorique, orientée recherche/masters) ou Professionnelle / Sciences & Techniques (orientée métier ou cycles spécialisés)."
    },
    {
      letter: "M",
      title: "Master (Bac +5)",
      color: "bg-blue-600",
      desc: "Se prépare en 4 semestres (S1 à S4) après la Licence. Permet une spécialisation pointue : Master Fondamental, Master Spécialisé ou Master Sciences et Techniques (MST)."
    },
    {
      letter: "D",
      title: "Doctorat (Bac +8)",
      color: "bg-indigo-600",
      desc: "Se prépare en 3 ans minimum après le Master. Centré sur la recherche scientifique et la soutenance d'une thèse au sein d'un laboratoire accrédité."
    }
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-12 animate-fade-in px-4">
      {/* 1. Système LMD */}
      <section className="space-y-6">
        <div className="border-b border-acem-border pb-3">
          <h2 className="font-title text-xl sm:text-2xl font-bold text-acem-primary-dark">
            1. Le système académique LMD au Maroc
          </h2>
          <p className="text-sm text-acem-muted">
            Architecture de l'enseignement supérieur structurée en trois grades pour harmoniser les diplômes et faciliter la mobilité internationale.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {lmdGrades.map((grade, idx) => (
            <Card key={idx} variant="elevated" className="p-6 space-y-3">
              <div className="flex items-center gap-3">
                <span className={`w-10 h-10 rounded-xl ${grade.color} text-white font-title text-lg font-bold flex items-center justify-center shadow-sm`}>
                  {grade.letter}
                </span>
                <h3 className="font-title text-base font-bold text-acem-text">
                  {grade.title}
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-acem-text/80 leading-relaxed">
                {grade.desc}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* 2. Poursuites d'études par établissement */}
      <section className="space-y-6">
        <div className="border-b border-acem-border pb-3">
          <h2 className="font-title text-xl sm:text-2xl font-bold text-acem-primary-dark">
            2. Poursuites d'études & Masters à Fès
          </h2>
          <p className="text-sm text-acem-muted">
            Débouchés et filières d'excellence après l'obtention de la Licence dans les établissements de l'USMBA.
          </p>
        </div>

        {/* FST */}
        <Card variant="elevated" className="p-6 space-y-4">
          <div className="flex items-center gap-3 border-b border-acem-border/60 pb-3">
            <span className="bg-acem-primary text-white text-xs px-2.5 py-1 rounded-md font-bold uppercase">FST</span>
            <h3 className="font-title text-lg font-bold text-acem-primary-dark">
              Faculté des Sciences et Techniques
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-acem-surface-container-low p-4 rounded-xl border border-acem-border/60 space-y-2">
              <h4 className="font-bold text-emerald-800 text-sm">Masters Sciences et Techniques (MST)</h4>
              <ul className="text-xs sm:text-sm text-acem-text/90 space-y-1 list-disc list-inside">
                <li>Génie Industriel (GIND)</li>
                <li>Génie Mécanique et Productique (GMPR)</li>
                <li>Ingénierie Électrique & Systèmes Embarqués (IESE)</li>
                <li>Mathématiques & Calcul Scientifique (MACS)</li>
                <li>Sciences des Données & Systèmes Intelligents (SDSI)</li>
                <li>Chimie des Molécules Bioactives (CMBA)</li>
                <li>Génie des Matériaux et Procédés (GMP)</li>
                <li>Biotechnologie Microbienne (BM)</li>
                <li>Gestion et Conservation de la Biodiversité (GCB)</li>
              </ul>
            </div>

            <div className="bg-acem-surface-container-low p-4 rounded-xl border border-acem-border/60 space-y-2">
              <h4 className="font-bold text-amber-800 text-sm">Cycles Ingénieur (Passerelles)</h4>
              <p className="text-xs text-acem-muted italic">Sur concours & sélection de dossier (mentions requises) :</p>
              <ul className="text-xs sm:text-sm text-acem-text/90 space-y-1 list-disc list-inside">
                <li>Systèmes Intelligents, Communicants et Mobiles (SICoM)</li>
                <li>Industries Agro-Alimentaires (IAA)</li>
                <li>Génie Mécanique (GM)</li>
                <li>Systèmes Mécatroniques & Ingénierie Automobile (SMIA)</li>
                <li>Sciences Analytiques, Chimiométrie & Qualimétrie (ISACQ)</li>
                <li>Génie Civil (GC)</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* FSDM */}
        <Card variant="elevated" className="p-6 space-y-4">
          <div className="flex items-center gap-3 border-b border-acem-border/60 pb-3">
            <span className="bg-acem-primary text-white text-xs px-2.5 py-1 rounded-md font-bold uppercase">FSDM</span>
            <h3 className="font-title text-lg font-bold text-acem-primary-dark">
              Faculté des Sciences Dhar El Mahraz
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-acem-surface-container-low p-4 rounded-xl border border-acem-border/60 space-y-2">
              <h4 className="font-bold text-blue-800 text-sm">Masters Recherche & Professionnels</h4>
              <ul className="text-xs sm:text-sm text-acem-text/90 space-y-1 list-disc list-inside">
                <li>Qualité du Logiciel (MQL)</li>
                <li>Web Intelligence et Sciences des Données (WISD)</li>
                <li>Big Data & Systèmes Intelligents (BDSI)</li>
                <li>Machine Learning Avancé & Multimédia (MLAIM)</li>
                <li>Smart Industry (M2SI)</li>
                <li>Automatique & Énergies Renouvelables (MAER)</li>
                <li>Biotechnologie Médicale & Innovation (BIOMSSI)</li>
                <li>Bio-informatique & IA pour la Médecine (BIAM)</li>
                <li>Chimie Analytique & Environnement (CAE)</li>
              </ul>
            </div>

            <div className="bg-acem-surface-container-low p-4 rounded-xl border border-acem-border/60 space-y-2">
              <h4 className="font-bold text-purple-800 text-sm">Licences & Filières d'Excellence</h4>
              <ul className="text-xs sm:text-sm text-acem-text/90 space-y-1 list-disc list-inside">
                <li>Systèmes Connectés, Embarqués & Mécatronique (SCEM)</li>
                <li>Physique Médicale & Technologies de Radioprotection (PMTR)</li>
                <li>Génie Biomédical & Technologie Digitale (GBTD)</li>
                <li>Biotechnologie & Sécurité Sanitaire des Aliments (BA2SA)</li>
                <li>Biotechnologie Alimentaire & Qualité (BAEMQ)</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* FSJES & FLDM & Grandes Écoles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card variant="elevated" className="p-5 space-y-3">
            <h4 className="font-title font-bold text-acem-primary text-base border-b border-acem-border/50 pb-2">
              FSJES (Éco & Droit)
            </h4>
            <ul className="text-xs text-acem-text/90 space-y-1.5 list-disc list-inside">
              <li>Ingénierie Financière & Banque</li>
              <li>Comptabilité, Contrôle & Audit (CCA)</li>
              <li>Management des RH & Stratégie</li>
              <li>Marketing & Business Administration</li>
              <li>Logistique & Commerce International</li>
              <li>Juriste d'Affaires & Droit Numérique</li>
              <li>Droit Foncier & Contrats</li>
            </ul>
          </Card>

          <Card variant="elevated" className="p-5 space-y-3">
            <h4 className="font-title font-bold text-acem-primary text-base border-b border-acem-border/50 pb-2">
              Grandes Écoles (ENCG, ENSA, EST)
            </h4>
            <ul className="text-xs text-acem-text/90 space-y-1.5 list-disc list-inside">
              <li><strong>ENCG :</strong> Passerelles Commerce/Gestion, Masters Spécialisés</li>
              <li><strong>ENSA :</strong> Génie Info, Industriel, Mécanique, GESI</li>
              <li><strong>EST :</strong> Licences Pro Cyber-sécurité, Réseaux, Gestion</li>
            </ul>
          </Card>

          <Card variant="elevated" className="p-5 space-y-3">
            <h4 className="font-title font-bold text-acem-primary text-base border-b border-acem-border/50 pb-2">
              Santé & Lettres (FMPMD, FLDM)
            </h4>
            <ul className="text-xs text-acem-text/90 space-y-1.5 list-disc list-inside">
              <li><strong>Santé :</strong> Masters Santé Publique, Soins Infirmiers, DU</li>
              <li><strong>FLDM :</strong> Études Francophones, Communication, Sociologie</li>
            </ul>
          </Card>
        </div>
      </section>
    </div>
  );
}
